import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styles from "./PounceQualifyingQuestions.module.css";
import {
    getPounceAgentSettings,
    updatePounceAgentSettings,
    suggestPounceVariable,
} from "../../Store/apiStore";

const MAX_OPTIONS = 10;

const QUESTION_TYPES = [
    {
        label: "Multiple Choice",
        value: "Multiple Choice",
        description: "Lead can choose one or multiple options",
        icon: (
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="9" y1="6" x2="20" y2="6"></line>
                <line x1="9" y1="12" x2="20" y2="12"></line>
                <line x1="9" y1="18" x2="20" y2="18"></line>
                <circle cx="4.5" cy="6" r="1.4" fill="currentColor"></circle>
                <circle cx="4.5" cy="12" r="1.4" fill="currentColor"></circle>
                <circle cx="4.5" cy="18" r="1.4" fill="currentColor"></circle>
            </svg>
        ),
    },
    {
        label: "Single Select",
        value: "Single Select",
        description: "Lead selects only one option from list",
        icon: (
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3.5" y="3.5" width="17" height="17" rx="4"></rect>
                <polyline points="8.5 12.2 11.2 15 15.8 9.5"></polyline>
            </svg>
        ),
    },
    {
        label: "Yes / No",
        value: "Yes / No",
        description: "Simple binary confirmation question",
        icon: (
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <rect x="2" y="6.5" width="20" height="11" rx="5.5"></rect>
                <circle cx="8" cy="12" r="3" fill="currentColor"></circle>
            </svg>
        ),
    },
    {
        label: "Free Text",
        value: "Free Text",
        description: "Lead speaks or enters their custom answer",
        icon: (
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="4" y1="6" x2="20" y2="6"></line>
                <line x1="4" y1="12" x2="16" y2="12"></line>
                <line x1="4" y1="18" x2="18" y2="18"></line>
            </svg>
        ),
    },
];

export const PounceQualifyingQuestions = () => {
    const navigate = useNavigate();
    const { agentId: paramAgentId } = useParams();
    const agentId =
        paramAgentId ||
        sessionStorage.getItem("SelectAgentId") ||
        localStorage.getItem("SelectAgentId") ||
        "";

    // Navigation states: 'list' | 'add_edit' | 'saved'
    const [currentScreen, setCurrentScreen] = useState("list");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [serverError, setServerError] = useState(null);

    // Real question list from backend API
    const [questions, setQuestions] = useState([]);
    const [templateName, setTemplateName] = useState("Qualifying Questions");

    // Add / Edit Form State
    const [editingQuestionId, setEditingQuestionId] = useState(null);
    const [formType, setFormType] = useState("Multiple Choice");
    const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
    const typeDropdownRef = useRef(null);

    const [formText, setFormText] = useState("");
    const [formOptions, setFormOptions] = useState(["", ""]);
    const [formRequired, setFormRequired] = useState(true);
    const [formIsDisqualifier, setFormIsDisqualifier] = useState(false);
    const [formDisqualifyAnswer, setFormDisqualifyAnswer] = useState("");
    const [formVariable, setFormVariable] = useState("");
    const [formError, setFormError] = useState("");

    // AI Variable state
    const [originalVariable, setOriginalVariable] = useState("");
    const [newGeneratedVariable, setNewGeneratedVariable] = useState("");
    const [isSuggestingVariable, setIsSuggestingVariable] = useState(false);
    const [isEditingVariable, setIsEditingVariable] = useState(false);
    const initialQuestionTextRef = useRef("");

    /**
     * Helper: normalizes questions directly from backend data shape
     */
    const normalizeQuestions = (list) => {
        if (!Array.isArray(list)) return [];
        return list.map((q, idx) => {
            const qText = q.question || q.text || "";
            const qType = String(q.type || "multiple_choice").toLowerCase();
            let qOptions = Array.isArray(q.options) ? [...q.options] : [];
            if (qType === "yes_no" && qOptions.length === 0) {
                qOptions = ["Yes", "No"];
            }
            const isDisq = Boolean(q.isDisqualifier || q.hasDisqualifier);
            const disqAns =
                q.disqualifyAnswer ||
                q.disqualifierValue ||
                (Array.isArray(q.disqualifyingValues) && q.disqualifyingValues[0]) ||
                (Array.isArray(q.disqualifyValues) && q.disqualifyValues[0]) ||
                "";

            return {
                ...q,
                id: q.id || `q_${Date.now()}_${idx}`,
                question: qText,
                text: qText,
                type: qType,
                options: qOptions,
                isDisqualifier: isDisq,
                disqualifyAnswer: disqAns,
                disqualifyingValues: q.disqualifyingValues || (disqAns ? [disqAns] : []),
                variable: q.variable || "",
                order: q.order !== undefined ? q.order : idx + 1,
                required: q.required !== undefined ? q.required : true,
            };
        });
    };

    // 1. Fetch real agent questions from backend on mount or agent change
    useEffect(() => {
        loadSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [agentId]);

    const loadSettings = async () => {
        try {
            setLoading(true);
            setServerError(null);
            const res = await getPounceAgentSettings(agentId);

            if (res && res.success && res.data) {
                const raw =
                    res.data.qualifyingQuestions ||
                    res.data.qualifying_questions ||
                    res.data.pounce?.qualifyingQuestions ||
                    [];
                setQuestions(normalizeQuestions(raw));
                if (res.data.qualifyingTemplate) {
                    setTemplateName(res.data.qualifyingTemplate);
                }
            } else if (res && Array.isArray(res.qualifyingQuestions)) {
                setQuestions(normalizeQuestions(res.qualifyingQuestions));
                if (res.qualifyingTemplate) {
                    setTemplateName(res.qualifyingTemplate);
                }
            } else {
                setQuestions([]);
            }
        } catch (err) {
            console.error("loadSettings error:", err);
            setServerError(
                err?.response?.data?.message ||
                err?.message ||
                "Unable to fetch qualifying questions from server."
            );
            setQuestions([]);
        } finally {
            setLoading(false);
        }
    };

    // Close custom Question Type dropdown on outside click or escape
    useEffect(() => {
        if (!typeDropdownOpen) return;
        const handleOutsideClick = (e) => {
            if (typeDropdownRef.current && !typeDropdownRef.current.contains(e.target)) {
                setTypeDropdownOpen(false);
            }
        };
        const handleKeyDown = (e) => {
            if (e.key === "Escape") setTypeDropdownOpen(false);
        };
        document.addEventListener("mousedown", handleOutsideClick);
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [typeDropdownOpen]);

    // Debounced API call (~0.5s after user stops typing) to suggest variable name
    useEffect(() => {
        if (currentScreen !== "add_edit") return;

        const trimmed = formText.trim();
        if (!trimmed) {
            if (!editingQuestionId) {
                setFormVariable("");
                setNewGeneratedVariable("");
            }
            setIsSuggestingVariable(false);
            return;
        }

        // In edit mode: if question text hasn't been modified yet, keep the DB variable
        if (editingQuestionId && initialQuestionTextRef.current && trimmed === initialQuestionTextRef.current.trim()) {
            setIsSuggestingVariable(false);
            return;
        }

        setIsSuggestingVariable(true);

        const timer = setTimeout(async () => {
            try {
                const taken = questions
                    .filter((q) => q.id !== editingQuestionId)
                    .map((q) => q.variable)
                    .filter(Boolean);

                const res = await suggestPounceVariable(trimmed, taken);
                console.log("suggestPounceVariable response:", res);

                const generated =
                    res?.variable ||
                    res?.data?.variable ||
                    (typeof res === "string" ? res : "");

                if (generated) {
                    if (editingQuestionId && originalVariable) {
                        // When editing an existing question: compare with DB variable
                        if (generated !== originalVariable) {
                            setNewGeneratedVariable(generated);
                            setFormVariable(generated);
                        } else {
                            setNewGeneratedVariable("");
                            setFormVariable(originalVariable);
                        }
                    } else {
                        // When adding a new question: only new variable generated
                        setFormVariable(generated);
                        setNewGeneratedVariable("");
                    }
                }
            } catch (err) {
                console.warn("Variable suggestion error:", err);
                const fallbackVar = trimmed
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, "_")
                    .replace(/^_+|_+$/g, "")
                    .slice(0, 32);
                if (fallbackVar) {
                    if (editingQuestionId && originalVariable && fallbackVar !== originalVariable) {
                        setNewGeneratedVariable(fallbackVar);
                        setFormVariable(fallbackVar);
                    } else {
                        setFormVariable(fallbackVar);
                    }
                }
            } finally {
                setIsSuggestingVariable(false);
            }
        }, 500); // 0.5s debounce

        return () => clearTimeout(timer);
    }, [formText, currentScreen, editingQuestionId, originalVariable, questions]);

    // Question Type display badge matching screenshot
    const renderQuestionTypeDisplay = (type) => {
        const norm = String(type || "").toLowerCase();

        if (norm === "multiple_choice") {
            return (
                <div className={styles.typeRow}>
                    <svg className={styles.typeIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <line x1="9" y1="6" x2="20" y2="6"></line>
                        <line x1="9" y1="12" x2="20" y2="12"></line>
                        <line x1="9" y1="18" x2="20" y2="18"></line>
                        <circle cx="4.5" cy="6" r="1.2" fill="currentColor" stroke="none"></circle>
                        <circle cx="4.5" cy="12" r="1.2" fill="currentColor" stroke="none"></circle>
                        <circle cx="4.5" cy="18" r="1.2" fill="currentColor" stroke="none"></circle>
                    </svg>
                    <span>Multiple choice</span>
                </div>
            );
        }

        if (norm === "single_select") {
            return (
                <div className={styles.typeRow}>
                    <svg className={styles.typeIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3.5" y="3.5" width="17" height="17" rx="3.5"></rect>
                        <polyline points="8.5 12.2 11.2 15 15.8 9.5"></polyline>
                    </svg>
                    <span>Single select</span>
                </div>
            );
        }

        if (norm === "yes_no") {
            return (
                <div className={styles.typeRow}>
                    <svg className={styles.typeIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <rect x="2" y="6.5" width="20" height="11" rx="5.5"></rect>
                        <circle cx="8" cy="12" r="3" fill="currentColor" stroke="none"></circle>
                    </svg>
                    <span>Yes / No</span>
                </div>
            );
        }

        return (
            <div className={styles.typeRow}>
                <svg className={styles.typeIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <line x1="4" y1="6" x2="20" y2="6"></line>
                    <line x1="4" y1="12" x2="14" y2="12"></line>
                    <line x1="4" y1="18" x2="18" y2="18"></line>
                </svg>
                <span>Free text</span>
            </div>
        );
    };

    // Open Edit Form with real question data
    const handleOpenEdit = (q) => {
        setEditingQuestionId(q.id);
        const text = q.question || q.text || "";
        setFormText(text);
        initialQuestionTextRef.current = text;
        const rawType = String(q.type || "").toLowerCase();
        if (rawType === "single_select") setFormType("Single Select");
        else if (rawType === "yes_no") setFormType("Yes / No");
        else if (rawType === "free_text" || rawType === "text") setFormType("Free Text");
        else setFormType("Multiple Choice");

        const realOpts = Array.isArray(q.options) && q.options.length > 0 ? [...q.options] : ["", ""];
        setFormOptions(realOpts);
        setFormRequired(q.required !== undefined ? q.required : true);
        setFormIsDisqualifier(Boolean(q.isDisqualifier));
        setFormDisqualifyAnswer(q.disqualifyAnswer || "");

        const dbVar = q.variable || "";
        setOriginalVariable(dbVar);
        setNewGeneratedVariable("");
        setFormVariable(dbVar);

        setIsEditingVariable(false);
        setIsSuggestingVariable(false);
        setFormError("");
        setTypeDropdownOpen(false);
        setCurrentScreen("add_edit");
    };

    // Open Add Question Form with clean empty inputs
    const handleOpenAdd = () => {
        setEditingQuestionId(null);
        setFormText("");
        initialQuestionTextRef.current = "";
        setFormType("Multiple Choice");
        setFormOptions(["", ""]);
        setFormRequired(true);
        setFormIsDisqualifier(false);
        setFormDisqualifyAnswer("");

        setOriginalVariable("");
        setNewGeneratedVariable("");
        setFormVariable("");

        setIsEditingVariable(false);
        setIsSuggestingVariable(false);
        setFormError("");
        setTypeDropdownOpen(false);
        setCurrentScreen("add_edit");
    };

    // Type change handler
    const handleSelectType = (selectedType) => {
        setFormType(selectedType);
        setTypeDropdownOpen(false);
        if (selectedType === "Yes / No") {
            setFormDisqualifyAnswer("No");
        } else if (selectedType === "Multiple Choice" || selectedType === "Single Select") {
            if (!Array.isArray(formOptions) || formOptions.length < 2) {
                setFormOptions(["", ""]);
            }
        }
    };

    // Option actions with max 10 limit
    const handleAddOption = () => {
        if (formOptions.length >= MAX_OPTIONS) {
            setFormError(`Maximum of ${MAX_OPTIONS} options allowed.`);
            return;
        }
        setFormOptions((prev) => [...prev, ""]);
        setFormError("");
    };

    const handleOptionChange = (idx, value) => {
        setFormOptions((prev) => {
            const next = [...prev];
            next[idx] = value;
            return next;
        });
        if (formError && formOptions.length <= MAX_OPTIONS) {
            setFormError("");
        }
    };

    const handleRemoveOption = (idx) => {
        if (formOptions.length <= 1) return;
        const removedVal = formOptions[idx];
        if (formDisqualifyAnswer === removedVal) {
            setFormDisqualifyAnswer("");
        }
        setFormOptions((prev) => prev.filter((_, i) => i !== idx));
        setFormError("");
    };

    // Save handler writing real data to backend
    const handleSaveQuestion = async () => {
        const trimmedText = formText.trim();
        if (!trimmedText) {
            setFormError("Question text cannot be empty.");
            return;
        }
        if (trimmedText.length > 200) {
            setFormError("Question text must be 200 characters or fewer.");
            return;
        }

        let apiType = "multiple_choice";
        if (formType === "Single Select") apiType = "single_select";
        else if (formType === "Yes / No") apiType = "yes_no";
        else if (formType === "Free Text") apiType = "free_text";

        const isChoiceType = apiType === "multiple_choice" || apiType === "single_select";

        let finalOptions = [];
        if (apiType === "yes_no") {
            finalOptions = ["Yes", "No"];
        } else if (isChoiceType) {
            finalOptions = formOptions.map((o) => o.trim()).filter(Boolean);
            if (finalOptions.length < 2) {
                setFormError("Please provide at least 2 valid answer options.");
                return;
            }
            if (finalOptions.length > MAX_OPTIONS) {
                setFormError(`Maximum of ${MAX_OPTIONS} options allowed.`);
                return;
            }
        }

        if (formIsDisqualifier) {
            if (!formDisqualifyAnswer) {
                setFormError("Please select the answer that marks lead as disqualified.");
                return;
            }
            if (isChoiceType && !finalOptions.includes(formDisqualifyAnswer)) {
                setFormError("Disqualifier answer must match one of your configured options.");
                return;
            }
        }

        setSaving(true);
        setFormError("");

        try {
            const questionObj = {
                id: editingQuestionId || `q_${Date.now()}`,
                question: trimmedText,
                text: trimmedText,
                type: apiType,
                options: finalOptions,
                required: formRequired,
                isDisqualifier: formIsDisqualifier,
                disqualifyAnswer: formIsDisqualifier ? formDisqualifyAnswer : "",
                disqualifyingValues: formIsDisqualifier && formDisqualifyAnswer ? [formDisqualifyAnswer] : [],
                variable: formVariable || trimmedText.toLowerCase().replace(/[^a-z0-9]+/g, "_").slice(0, 32),
            };

            let updatedList = [];
            if (editingQuestionId) {
                updatedList = questions.map((q) =>
                    q.id === editingQuestionId ? { ...q, ...questionObj } : q
                );
            } else {
                updatedList = [...questions, questionObj];
            }

            // Persist real data directly to backend
            const res = await updatePounceAgentSettings(agentId, {
                qualifyingQuestions: updatedList,
                qualifyingTemplate: templateName,
            });

            const serverList =
                res?.data?.qualifyingQuestions ||
                res?.qualifyingQuestions ||
                updatedList;

            setQuestions(normalizeQuestions(serverList));
            setCurrentScreen("saved");
        } catch (saveErr) {
            console.error("Save error:", saveErr);
            setFormError(
                saveErr?.response?.data?.message ||
                saveErr?.message ||
                "Failed to save question to server. Please try again."
            );
        } finally {
            setSaving(false);
        }
    };

    // Delete question handler
    const handleDeleteQuestion = async () => {
        if (!editingQuestionId) return;
        if (!window.confirm("Are you sure you want to delete this question?")) return;

        setSaving(true);
        try {
            const updatedList = questions.filter((q) => q.id !== editingQuestionId);
            const res = await updatePounceAgentSettings(agentId, {
                qualifyingQuestions: updatedList,
                qualifyingTemplate: templateName,
            });

            const serverList =
                res?.data?.qualifyingQuestions ||
                res?.qualifyingQuestions ||
                updatedList;

            setQuestions(normalizeQuestions(serverList));
            setCurrentScreen("list");
        } catch (err) {
            console.error("Delete error:", err);
            setFormError(
                err?.response?.data?.message ||
                "Failed to delete question from server."
            );
        } finally {
            setSaving(false);
        }
    };

    // ──────────────────────────────────────────────────────────────────────────
    // SCREEN 1: QUESTION LIST VIEW (Clean view without drag handle or template dropdown)
    // ──────────────────────────────────────────────────────────────────────────
    if (currentScreen === "list") {
        return (
            <div className={styles.page}>
                {/* Header */}
                <div className={styles.header}>
                    <button
                        type="button"
                        className={styles.backBtn}
                        onClick={() => navigate(-1)}
                        aria-label="Back"
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="19" y1="12" x2="5" y2="12"></line>
                            <polyline points="12 19 5 12 12 5"></polyline>
                        </svg>
                    </button>
                    <h1 className={styles.title}>Qualifying Questions</h1>
                    <p className={styles.subtitle}>
                        Set the questions Pounce asks before booking.
                    </p>
                </div>

                <div className={styles.contentWrap}>
                    {/* Action Row with Add Question button */}
                    <div className={styles.actionRow}>
                        <button
                            type="button"
                            className={styles.addQuestionBtn}
                            onClick={handleOpenAdd}
                        >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                <line x1="12" y1="5" x2="12" y2="19"></line>
                                <line x1="5" y1="12" x2="19" y2="12"></line>
                            </svg>
                            <span>Add Question</span>
                        </button>
                    </div>

                    {/* Server Error Notification if API call failed */}
                    {serverError && (
                        <div className={styles.serverErrorBox}>
                            <span>{serverError}</span>
                            <button type="button" className={styles.retryBtn} onClick={loadSettings}>
                                Retry
                            </button>
                        </div>
                    )}

                    {/* Loading state */}
                    {loading ? (
                        <div className={styles.loading}>Loading questions...</div>
                    ) : questions.length === 0 ? (
                        /* Empty state when no questions exist in backend yet */
                        <div className={styles.emptyCard}>
                            <p className={styles.emptyTitle}>No qualifying questions yet</p>
                            <p className={styles.emptySubtitle}>
                                Add questions for Pounce to ask before booking an appointment.
                            </p>
                            <button
                                type="button"
                                className={styles.emptyAddBtn}
                                onClick={handleOpenAdd}
                            >
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                    <line x1="12" y1="5" x2="12" y2="19"></line>
                                    <line x1="5" y1="12" x2="19" y2="12"></line>
                                </svg>
                                <span>Add Your First Question</span>
                            </button>
                        </div>
                    ) : (
                        /* Real Question Cards List (NO 6 dots drag handles) */
                        <div className={styles.questionList}>
                            {questions.map((q, idx) => (
                                <div key={q.id || idx} className={styles.questionCard}>
                                    {/* Question Number Badge */}
                                    <div className={styles.numberCircle}>{idx + 1}</div>

                                    {/* Question Content */}
                                    <div className={styles.cardContent}>
                                        <p className={styles.questionText}>{q.question || q.text}</p>

                                        {/* Disqualifier Badge if marked as disqualifier */}
                                        {q.isDisqualifier && (
                                            <span className={styles.disqualifierBadge}>
                                                Disqualifier rule
                                            </span>
                                        )}

                                        {/* Question Type Display & Database Variable Badge */}
                                        <div className={styles.metaRow}>
                                            {renderQuestionTypeDisplay(q.type)}
                                            {q.variable && (
                                                <span
                                                    className={styles.listVariableBadge}
                                                    title={`CRM Variable: {${q.variable}}`}
                                                >
                                                    <span className={styles.listVariableBrace}>&#123;</span>
                                                    {q.variable}
                                                    <span className={styles.listVariableBrace}>&#125;</span>
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Pencil Edit Icon */}
                                    <button
                                        type="button"
                                        className={styles.editButton}
                                        onClick={() => handleOpenEdit(q)}
                                        aria-label={`Edit ${q.question || q.text}`}
                                    >
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
                                        </svg>
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Bottom Lavender Notice Banner */}
                    <div className={styles.infoBox}>
                        <div className={styles.infoIcon}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" y1="16" x2="12" y2="12" />
                                <line x1="12" y1="8" x2="12.01" y2="8" />
                            </svg>
                        </div>
                        <p className={styles.infoText}>
                            Booking happens only after required questions are answered.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // ──────────────────────────────────────────────────────────────────────────
    // SCREEN 2: ADD / EDIT QUESTION (Custom Dropdown + Pixel-Perfect UI/UX)
    // ──────────────────────────────────────────────────────────────────────────
    if (currentScreen === "add_edit") {
        const isChoiceType = formType === "Multiple Choice" || formType === "Single Select";
        const availableChoices = formType === "Yes / No" ? ["Yes", "No"] : formOptions;

        return (
            <div className={styles.page}>
                {/* Top Header Bar matching image: < on left, Save button on right */}
                <div className={styles.formHeaderBar}>
                    <button
                        type="button"
                        className={styles.backBtn}
                        onClick={() => setCurrentScreen("list")}
                        aria-label="Back"
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="15 18 9 12 15 6"></polyline>
                        </svg>
                    </button>

                    <button
                        type="button"
                        className={styles.saveHeaderBtn}
                        onClick={handleSaveQuestion}
                        disabled={saving}
                    >
                        {saving ? "Saving…" : "Save"}
                    </button>
                </div>

                <div className={styles.contentWrap}>
                    <h1 className={styles.formTitle}>
                        {editingQuestionId ? "Edit Question" : "Add Question"}
                    </h1>

                    {formError && (
                        <div className={styles.formErrorBox}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="12" y1="8" x2="12" y2="12"></line>
                                <line x1="12" y1="16" x2="12.01" y2="16"></line>
                            </svg>
                            <span>{formError}</span>
                        </div>
                    )}

                    {/* Question Type with Custom Styled Dropdown */}
                    <div className={styles.inputGroup}>
                        <label className={styles.inputLabel}>Question Type</label>
                        <div className={styles.selectWrapper} ref={typeDropdownRef}>
                            <button
                                type="button"
                                className={`${styles.customSelectTrigger} ${typeDropdownOpen ? styles.customSelectTriggerOpen : ""}`}
                                onClick={() => setTypeDropdownOpen(!typeDropdownOpen)}
                                aria-haspopup="listbox"
                                aria-expanded={typeDropdownOpen}
                            >
                                <div className={styles.selectTriggerLeft}>
                                    <span className={styles.selectTriggerValue}>{formType}</span>
                                </div>
                                <svg
                                    className={`${styles.selectArrow} ${typeDropdownOpen ? styles.selectArrowOpen : ""}`}
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.4"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <polyline points="6 9 12 15 18 9"></polyline>
                                </svg>
                            </button>

                            {typeDropdownOpen && (
                                <div className={styles.customSelectMenu} role="listbox">
                                    {QUESTION_TYPES.map((t) => {
                                        const isSelected = formType === t.label;
                                        return (
                                            <button
                                                key={t.label}
                                                type="button"
                                                role="option"
                                                aria-selected={isSelected}
                                                className={`${styles.customSelectOption} ${isSelected ? styles.customSelectOptionSelected : ""}`}
                                                onClick={() => handleSelectType(t.label)}
                                            >
                                                <div className={styles.optionInfo}>
                                                    <div className={styles.optionTypeIcon}>{t.icon}</div>
                                                    <div className={styles.optionTextCol}>
                                                        <span className={styles.optionMainText}>{t.label}</span>
                                                        <span className={styles.optionDescText}>{t.description}</span>
                                                    </div>
                                                </div>
                                                {isSelected && (
                                                    <svg className={styles.optionCheckIcon} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
                                                        <polyline points="20 6 9 17 4 12"></polyline>
                                                    </svg>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Question Text with 200 char counter */}
                    <div className={styles.inputGroup}>
                        <label className={styles.inputLabel}>Question Text</label>
                        <div className={styles.textareaWrapper}>
                            <textarea
                                className={styles.textareaField}
                                rows={3}
                                placeholder="What service are you interested in?"
                                value={formText}
                                maxLength={200}
                                onChange={(e) => {
                                    setFormText(e.target.value);
                                }}
                            />
                            <span className={`${styles.charCount} ${formText.length >= 190 ? styles.charCountWarning : ""}`}>
                                {formText.length}/200
                            </span>
                        </div>
                    </div>

                    {/* AI Variable Suggestion Card - Blue Theme */}
                    <div className={styles.variableCard}>
                        <div className={styles.variableHeader}>
                            <div className={styles.variableTitleRow}>
                                <div className={styles.aiSparkleIcon}>
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8L12 2z" />
                                    </svg>
                                </div>
                                <span className={styles.variableLabel}>AI Variable Key</span>
                                {isSuggestingVariable && (
                                    <span className={styles.variableStatusGenerating}>
                                        <span className={styles.variableSpinner}></span>
                                        Suggesting...
                                    </span>
                                )}
                            </div>

                            {formVariable && !isEditingVariable && (
                                <button
                                    type="button"
                                    className={styles.variableEditToggleBtn}
                                    onClick={() => setIsEditingVariable(true)}
                                    title="Customize variable key"
                                >
                                    Edit
                                </button>
                            )}
                        </div>

                        {/* Variable Content */}
                        <div className={styles.variableContent}>
                            {isEditingVariable ? (
                                <div className={styles.variableInputWrapper}>
                                    <span className={styles.variableBracePrefix}>&#123;</span>
                                    <input
                                        type="text"
                                        className={styles.variableInputField}
                                        value={formVariable}
                                        placeholder="variable_name"
                                        onChange={(e) => {
                                            const val = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_");
                                            setFormVariable(val);
                                        }}
                                        onBlur={() => setIsEditingVariable(false)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") setIsEditingVariable(false);
                                        }}
                                        autoFocus
                                    />
                                    <span className={styles.variableBraceSuffix}>&#125;</span>
                                    <button
                                        type="button"
                                        className={styles.variableDoneBtn}
                                        onClick={() => setIsEditingVariable(false)}
                                    >
                                        Done
                                    </button>
                                </div>
                            ) : editingQuestionId && originalVariable && newGeneratedVariable && originalVariable !== newGeneratedVariable ? (
                                /* Minimal Old vs New Variable display: Clean & Compact */
                                <div className={styles.minimalChangeRow}>
                                    <div
                                        className={styles.variablePillDisplay}
                                        onClick={() => setIsEditingVariable(true)}
                                        title="Click to customize variable"
                                    >
                                        <span className={styles.variablePillBrace}>&#123;</span>
                                        <span className={styles.variablePillText}>{formVariable}</span>
                                        <span className={styles.variablePillBrace}>&#125;</span>
                                        <svg className={styles.variablePillPencil} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
                                        </svg>
                                    </div>

                                    <span className={styles.minimalPrevNotice}>
                                        (was &#123;{originalVariable}&#125;)
                                    </span>

                                    <button
                                        type="button"
                                        className={styles.minimalRevertBtn}
                                        onClick={() => {
                                            setFormVariable(formVariable === originalVariable ? newGeneratedVariable : originalVariable);
                                        }}
                                    >
                                        {formVariable === originalVariable ? `Use {${newGeneratedVariable}}` : `Keep {${originalVariable}}`}
                                    </button>
                                </div>
                            ) : formVariable ? (
                                /* Standard Single Variable Pill */
                                <div
                                    className={styles.variablePillDisplay}
                                    onClick={() => setIsEditingVariable(true)}
                                    title="Click to customize variable"
                                >
                                    <span className={styles.variablePillBrace}>&#123;</span>
                                    <span className={styles.variablePillText}>{formVariable}</span>
                                    <span className={styles.variablePillBrace}>&#125;</span>
                                    <svg className={styles.variablePillPencil} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
                                    </svg>
                                </div>
                            ) : isSuggestingVariable ? (
                                <div className={styles.variableLoadingPlaceholder}>
                                    <span className={styles.variablePulsingDot}></span>
                                    <span>Generating AI variable key from question...</span>
                                </div>
                            ) : (
                                <div className={styles.variableEmptyHint}>
                                    Type question text above to auto-generate a variable key.
                                </div>
                            )}
                        </div>

                        <p className={styles.variableHelpText}>
                            Extracted during call qualification and saved into lead CRM payload.
                        </p>
                    </div>

                    {/* Answer Options (capped at maximum 10) */}
                    {isChoiceType && (
                        <div className={styles.inputGroup}>
                            <label className={styles.inputLabel}>Answer Options</label>
                            <div className={styles.optionsStack}>
                                {formOptions.map((opt, idx) => (
                                    <div key={idx} className={styles.optionInputRow}>
                                        {/* 6 dot icon matching screenshot for answer options */}
                                        <div className={styles.optionGrip} title="Answer option">
                                            <svg width="12" height="16" viewBox="0 0 10 16" fill="#9CA3AF">
                                                <circle cx="2.5" cy="2.5" r="1.5" />
                                                <circle cx="2.5" cy="8" r="1.5" />
                                                <circle cx="2.5" cy="13.5" r="1.5" />
                                                <circle cx="7.5" cy="2.5" r="1.5" />
                                                <circle cx="7.5" cy="8" r="1.5" />
                                                <circle cx="7.5" cy="13.5" r="1.5" />
                                            </svg>
                                        </div>

                                        <input
                                            type="text"
                                            className={styles.optionTextField}
                                            value={opt}
                                            placeholder={`Option ${idx + 1}`}
                                            onChange={(e) => handleOptionChange(idx, e.target.value)}
                                        />

                                        {formOptions.length > 1 && (
                                            <button
                                                type="button"
                                                className={styles.trashIconButton}
                                                onClick={() => handleRemoveOption(idx)}
                                                aria-label="Delete option"
                                            >
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                                    <polyline points="3 6 5 6 21 6"></polyline>
                                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                                </svg>
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {/* Add Option button limited to 10 max */}
                            <div className={styles.addOptionContainer}>
                                <button
                                    type="button"
                                    className={`${styles.addOptionTextBtn} ${formOptions.length >= MAX_OPTIONS ? styles.disabledBtn : ""}`}
                                    onClick={handleAddOption}
                                    disabled={formOptions.length >= MAX_OPTIONS}
                                >
                                    <span className={styles.plusSign}>+</span>
                                    <span>Add Option</span>
                                </button>

                                {formOptions.length >= MAX_OPTIONS && (
                                    <span className={styles.optionLimitNotice}>
                                        Maximum of {MAX_OPTIONS} options reached
                                    </span>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Toggle 1: Make this question required */}
                    <div className={styles.toggleRow}>
                        <div className={styles.toggleTextGroup}>
                            <div className={styles.greenCheckBadge}>
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20 6 9 17 4 12"></polyline>
                                </svg>
                            </div>
                            <div>
                                <div className={styles.toggleHeading}>Make this question required</div>
                                <div className={styles.toggleSubheading}>Call cannot be booked without an answer</div>
                            </div>
                        </div>

                        <button
                            type="button"
                            className={`${styles.toggle} ${formRequired ? styles.on : ""}`}
                            onClick={() => setFormRequired(!formRequired)}
                            aria-label="Toggle make this question required"
                        >
                            <span className={styles.knob} />
                        </button>
                    </div>

                    {/* Toggle 2: Mark as disqualifier */}
                    <div className={styles.toggleRow}>
                        <div className={styles.toggleTextGroup}>
                            <div className={styles.greenCheckBadge}>
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20 6 9 17 4 12"></polyline>
                                </svg>
                            </div>
                            <div>
                                <div className={styles.toggleHeading}>Mark as disqualifier</div>
                                <div className={styles.toggleSubheading}>If the lead selects this, they will be marked as not qualified.</div>
                            </div>
                        </div>

                        <button
                            type="button"
                            className={`${styles.toggle} ${formIsDisqualifier ? styles.on : ""}`}
                            onClick={() => {
                                const next = !formIsDisqualifier;
                                setFormIsDisqualifier(next);
                                if (next && !formDisqualifyAnswer && formType === "Yes / No") {
                                    setFormDisqualifyAnswer("No");
                                }
                            }}
                            aria-label="Toggle mark as disqualifier"
                        >
                            <span className={styles.knob} />
                        </button>
                    </div>

                    {/* Disqualifier option chips when active */}
                    {formIsDisqualifier && formType !== "Free Text" && (
                        <div className={styles.disqualifierSelectorBox}>
                            <label className={styles.selectorHint}>Select the answer that marks lead as disqualified:</label>
                            <div className={styles.selectorChips}>
                                {availableChoices.filter(Boolean).map((opt) => (
                                    <button
                                        key={opt}
                                        type="button"
                                        className={`${styles.disqChip} ${formDisqualifyAnswer === opt ? styles.disqChipActive : ""}`}
                                        onClick={() => setFormDisqualifyAnswer(opt)}
                                    >
                                        {formDisqualifyAnswer === opt && (
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="20 6 9 17 4 12"></polyline>
                                            </svg>
                                        )}
                                        <span>{opt}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Optional Delete Question button when editing */}
                    {editingQuestionId && (
                        <button
                            type="button"
                            className={styles.deleteQuestionBtn}
                            onClick={handleDeleteQuestion}
                            disabled={saving}
                        >
                            Delete Question
                        </button>
                    )}
                </div>
            </div>
        );
    }

    // ──────────────────────────────────────────────────────────────────────────
    // SCREEN 3: QUESTION SAVED CONFIRMATION (Exact 1:1 match to Screenshot 3)
    // ──────────────────────────────────────────────────────────────────────────
    if (currentScreen === "saved") {
        return (
            <div className={styles.page}>
                <div className={styles.savedScreenWrapper}>
                    <div className={styles.savedTopGroup}>
                        {/* Confetti & Checkmark Graphic */}
                        <div className={styles.confettiArea}>
                            <div className={`${styles.confettiDot} ${styles.dotPurple1}`} />
                            <div className={`${styles.confettiDot} ${styles.dotPurple2}`} />
                            <div className={`${styles.confettiDot} ${styles.dotBlue1}`} />
                            <div className={`${styles.confettiDot} ${styles.dotPink1}`} />
                            <div className={`${styles.confettiDot} ${styles.dotPink2}`} />
                            <div className={`${styles.confettiDot} ${styles.dotGreen1}`} />
                            <div className={`${styles.confettiDot} ${styles.dotGreen2}`} />
                            <div className={`${styles.confettiDot} ${styles.dotAmber1}`} />
                            <div className={`${styles.confettiDot} ${styles.dotCyan1}`} />

                            <div className={styles.largeGreenCircle}>
                                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20 6 9 17 4 12"></polyline>
                                </svg>
                            </div>
                        </div>

                        <h2 className={styles.savedHeading}>Question Saved</h2>
                        <p className={styles.savedSubheading}>
                            Your question has been updated successfully.
                        </p>
                    </div>

                    <div className={styles.savedActions}>
                        <button
                            type="button"
                            className={styles.viewQuestionsSolidBtn}
                            onClick={() => setCurrentScreen("list")}
                        >
                            View Questions
                        </button>

                        <button
                            type="button"
                            className={styles.addAnotherGhostBtn}
                            onClick={handleOpenAdd}
                        >
                            Add Another Question
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return null;
};

export default PounceQualifyingQuestions;
